'use client';

import { Check, Warning } from '@phosphor-icons/react/ssr';
import { type ComponentProps, type ReactNode, useId } from 'react';

import { tv } from '../../lib/tv';
import { cn } from '../../lib/utils';

export type StepState = 'complete' | 'current' | 'upcoming' | 'error';
export type StepperOrientation = 'horizontal' | 'vertical';

/**
 * Figma Step (101:63). A 28px indicator: Complete = brand/strong with a 14px
 * icon/inverse check; Current = bg/surface, 2px brand/strong inside stroke, a
 * 4px brand/subtle-hover halo and a Label/Small text/brand number; Upcoming =
 * bg/surface, 1.5px border/strong inside stroke, text/tertiary number; Error
 * = status/danger/solid with a 14px warning. Label Body/Medium Strong
 * (Current: Label/Medium, 18 tall) - text/primary, Upcoming text/secondary,
 * Error status/danger/fg.
 * - Horizontal: gap 10, centred; no description. In a Stepper a 2px
 *   radius-1 connector fills the space to the next step (12 either side).
 * - Vertical: gap 12; a rail (gap 4) with the indicator and a 2px connector
 *   that fills the step's height; text 4 top / 24 bottom, gap 2, with a
 *   Body/Small text/secondary (Error: status/danger/fg) description. The
 *   connector turns brand/strong once the step is Complete, else
 *   border/default.
 */
export const stepStyles = tv({
  slots: {
    root: 'flex min-w-0',
    body: 'flex min-w-0 rounded-md text-left outline-none',
    indicator: 'flex size-7 shrink-0 items-center justify-center rounded-full',
    glyph: 'text-icon-inverse size-3.5',
    number: 'text-label-sm',
    rail: 'flex shrink-0 flex-col items-center gap-1 self-stretch',
    railConnector: 'w-0.5 flex-1 rounded-[1px]',
    text: 'flex min-w-0 flex-col',
    label: '',
    description: 'text-body-sm text-text-secondary',
    connector: 'h-0.5 min-w-4 flex-1 rounded-[1px]',
  },
  variants: {
    orientation: {
      horizontal: {
        root: 'items-center gap-3',
        body: 'items-center gap-2.5',
        label: 'whitespace-nowrap',
        // Figma hides the description in horizontal steps; keep it for assistive tech.
        description: 'sr-only',
      },
      vertical: {
        root: 'w-full',
        body: 'w-full items-start gap-3',
        text: 'flex-1 gap-0.5 pt-1 pb-6',
      },
    },
    state: {
      complete: {
        indicator: 'bg-brand-strong',
        label: 'text-body-md-strong text-text-primary',
      },
      current: {
        indicator:
          'bg-bg-surface inset-ring-2 inset-ring-brand-strong ring-4 ring-brand-subtle-hover',
        number: 'text-text-brand',
        label: 'text-label-md text-text-primary',
      },
      upcoming: {
        indicator: 'bg-bg-surface inset-ring-[1.5px] inset-ring-border-strong',
        number: 'text-text-tertiary',
        label: 'text-body-md-strong text-text-secondary',
      },
      error: {
        indicator: 'bg-status-danger-solid',
        label: 'text-body-md-strong text-status-danger-fg',
        description: 'text-status-danger-fg',
      },
    },
    done: {
      true: { connector: 'bg-brand-strong', railConnector: 'bg-brand-strong' },
      false: { connector: 'bg-border-default', railConnector: 'bg-border-default' },
    },
    clickable: {
      true: {
        body: 'cursor-pointer focus-visible:ring-2 focus-visible:ring-border-focus',
      },
    },
  },
  defaultVariants: { orientation: 'horizontal', state: 'upcoming', done: false },
});

const STATUS_TEXT: Partial<Record<StepState, string>> = {
  complete: 'completed',
  error: 'error',
};

export type StepProps = Omit<ComponentProps<'li'>, 'children' | 'onSelect'> & {
  /** Figma "State". Current sets `aria-current="step"`. */
  state?: StepState;
  /** Figma "Label" - one or two words. */
  label: ReactNode;
  /** Figma "Number" (Current / Upcoming indicator). */
  number: ReactNode;
  /** Vertical: the line under the label (the reason, for Error). Screen-reader only when horizontal. */
  description?: ReactNode;
  orientation?: StepperOrientation;
  /** Figma "Connector": the line to the next step. Off for the last step. */
  connector?: boolean;
  /** Makes the step a button (Complete steps: revisit). */
  onSelect?: () => void;
};

/**
 * One stage of a multi-step flow (Figma: Components ↳ Stepper ↳ Step). A list
 * item - render it in an `<ol>` (Stepper does). Its state is announced:
 * "Upload (completed)", "Customize" with `aria-current="step"`, "… (error)".
 */
export function Step({
  state = 'upcoming',
  label,
  number,
  description,
  orientation = 'horizontal',
  connector = false,
  onSelect,
  className,
  ...props
}: StepProps) {
  const styles = stepStyles({
    orientation,
    state,
    done: state === 'complete',
    clickable: Boolean(onSelect),
  });
  const Glyph = state === 'complete' ? Check : state === 'error' ? Warning : null;
  const indicator = (
    <span aria-hidden data-slot="step-indicator" className={styles.indicator()}>
      {Glyph ? (
        <Glyph className={styles.glyph()} />
      ) : (
        <span data-slot="step-number" className={styles.number()}>
          {number}
        </span>
      )}
    </span>
  );
  const status = STATUS_TEXT[state];
  const id = useId();
  const text = (
    <span data-slot="step-text" className={styles.text()}>
      <span id={`${id}-label`} data-slot="step-label" className={styles.label()}>
        {label}
        {/* Leading space inside: an sr-only span counts as a block in the name. */}
        {status && <span className="sr-only"> ({status})</span>}
      </span>
      {description && (
        <span
          id={`${id}-description`}
          data-slot="step-description"
          className={styles.description()}
        >
          {description}
        </span>
      )}
    </span>
  );
  const content =
    orientation === 'vertical' ? (
      <>
        <span data-slot="step-rail" className={styles.rail()}>
          {indicator}
          {connector && (
            <span
              aria-hidden
              data-slot="step-connector"
              className={styles.railConnector()}
            />
          )}
        </span>
        {text}
      </>
    ) : (
      <>
        {indicator}
        {text}
      </>
    );
  const Body = onSelect ? 'button' : 'div';

  return (
    <li
      data-slot="step"
      data-step-state={state}
      aria-current={state === 'current' ? 'step' : undefined}
      className={styles.root({
        className: [
          // grow (basis auto), not flex-1: the connectors share the spare width equally.
          orientation === 'horizontal' && connector ? 'grow' : 'shrink-0',
          className,
        ],
      })}
      {...props}
    >
      <Body
        data-slot="step-body"
        {...(onSelect
          ? {
              type: 'button' as const,
              onClick: onSelect,
              // Named by the label; the description describes it.
              'aria-labelledby': `${id}-label`,
              'aria-describedby': description ? `${id}-description` : undefined,
            }
          : {})}
        className={styles.body()}
      >
        {content}
      </Body>
      {orientation === 'horizontal' && connector && (
        <span aria-hidden data-slot="step-connector" className={styles.connector()} />
      )}
    </li>
  );
}

export type StepperStep = {
  label: ReactNode;
  /** Vertical: shown under the label. Horizontal: read to screen readers only. */
  description?: ReactNode;
  /** Override the state derived from `current` - e.g. `'error'`. */
  state?: StepState;
};

export type StepperProps = Omit<ComponentProps<'ol'>, 'children'> & {
  steps: StepperStep[];
  /** Index of the current step (0-based). Earlier steps are Complete, later ones Upcoming. */
  current: number;
  /** Figma "Orientation": Horizontal in wizard headers; Vertical in onboarding or side panels. */
  orientation?: StepperOrientation;
  /**
   * Revisit a finished step: Complete (and Error) steps become buttons that
   * call this with their index. Upcoming steps are never clickable.
   */
  onStepClick?: (index: number) => void;
  /** Names the list. Default "Progress". */
  'aria-label'?: string;
};

/**
 * Progress through a linear flow of 3–6 steps (Figma: Components ↳ Stepper;
 * not the Number Stepper). An `<ol>` of Steps - the current one has
 * `aria-current="step"`; connectors turn brand/strong up to the current step.
 * Horizontal: steps hug their labels, connectors share the rest of the width.
 */
export function Stepper({
  steps,
  current,
  orientation = 'horizontal',
  onStepClick,
  'aria-label': ariaLabel = 'Progress',
  className,
  ...props
}: StepperProps) {
  return (
    <ol
      data-slot="stepper"
      data-orientation={orientation}
      aria-label={ariaLabel}
      className={cn(
        orientation === 'horizontal' ? 'flex w-full items-center gap-3' : 'flex flex-col',
        className,
      )}
      {...props}
    >
      {steps.map((step, i) => {
        const state: StepState =
          step.state ??
          (i < current ? 'complete' : i === current ? 'current' : 'upcoming');
        const clickable = onStepClick && (state === 'complete' || state === 'error');
        return (
          <Step
            key={i}
            state={state}
            number={i + 1}
            label={step.label}
            description={step.description}
            orientation={orientation}
            connector={i < steps.length - 1}
            onSelect={clickable ? () => onStepClick(i) : undefined}
          />
        );
      })}
    </ol>
  );
}
